/* eslint-disable react/prop-types */
import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Box from "@material-ui/core/Box";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import { UPDATE_SUBSCRIPTION_PRICE } from "graphql/mutation/admin";
import { useStyles } from "Components/Modal/ModalStyles";
import { ReactComponent as CloseIcon } from "Assets/Svg/close.svg";

const validationSchema = yup.object({
  price: yup
    .number()
    .typeError("Price must be a number")
    .min(0, "Price cannot be negative")
    .required("Price is required"),
});

const EditPriceModal = ({ open, handleClose, subscription, handleOnSuccess }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [updateSubscriptionPrice] = useMutation(UPDATE_SUBSCRIPTION_PRICE);

  const handleUpdatePrice = async (values) => {
    try {
      setLoading(true);
      const { data } = await updateSubscriptionPrice({
        variables: {
          where: {
            id: subscription?.id,
          },
          input: {
            price: Number(values.price),
          },
        },
      });

      const { status, message } = data.updateSubscriptionPrice;

      if (status === "success") {
        toast.success(message || "Subscription price updated successfully");
        handleOnSuccess();
        handleClose();
      } else {
        toast.error(message || "Something went wrong !");
      }
      setLoading(false);
    } catch (err) {
      toast.error("Something went wrong !");
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      price: subscription?.price ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      handleUpdatePrice(values);
    },
  });

  const canSave = !loading && formik.dirty && formik.isValid;

  return (
    <Dialog
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      classes={{ paper: classes.dialogPaper1 }}
    >
      <DialogContent classes={{ root: classes.dialogContext }}>
        <div
          role="button"
          tabIndex={0}
          onKeyDown={loading ? undefined : handleClose}
          onClick={loading ? undefined : handleClose}
          className={classes.topBar}
        >
          <span className={classes.title}>Edit Price</span>
          <span className={`${classes.onHover} ${classes.closeBtn}`}>
            <CloseIcon />
          </span>
        </div>
        <Box id="alert-dialog-description">
          <form
            noValidate
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              formik.handleSubmit();
            }}
          >
            <Grid item style={{ marginTop: "3vh", padding: "0px 30px" }}>
              <Typography variant="body1" className={classes.subtitle}>
                Update the price for
                {" "}
                <b>{subscription?.name}</b>
              </Typography>
              <TextField
                id="price"
                name="price"
                type="number"
                label="Price"
                placeholder="Enter price"
                variant="outlined"
                fullWidth
                value={formik.values.price}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
              />
            </Grid>
            <Grid
              item
              style={{
                display: "flex",
                justifyContent: "space-evenly",
                marginTop: "4vh",
                marginBottom: "40px",
              }}
            >
              <Button
                variant="contained"
                size="small"
                style={{
                  width: "155px",
                  height: "40px",
                  fontWeight: "550",
                  color: "black",
                  boxShadow: "none",
                  fontSize: "14px",
                }}
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                type="submit"
                style={{
                  width: "155px",
                  height: "40px",
                  fontWeight: "550",
                  boxShadow: "none",
                  fontSize: "14px",
                  background: `var(--color-primary)`,
                  color: "white",
                  opacity: canSave ? 1 : 0.6,
                }}
                disabled={!canSave}
              >
                {loading && (
                  <CircularProgress
                    style={{
                      width: "20px",
                      height: "20px",
                      color: "#fff",
                      marginRight: "10px",
                    }}
                  />
                )}
                {loading ? "Saving..." : "Save"}
              </Button>
            </Grid>
          </form>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditPriceModal;
